import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { connect } from 'react-redux';
import {
  Message, Icon, Popup, Grid, Table, Statistic, Button, Modal, Divider,
} from 'semantic-ui-react';
import { Line } from 'react-chartjs-2';
import toast from 'react-hot-toast';
import moment from 'moment';
import api from 'api';
import util from 'utils/utils';
import useTheme from 'theme/useTheme';
import QRCodeStyling from 'qr-code-styling';
import { ThemedButton } from 'components/lib/UI';

import AppIcon from 'src/images/appIcon.png';

const domain = import.meta.env.DEV ? 'http://localhost:7090' : 'https://sdi.click';
const chartOptions = {
  layout: {
    padding: {
      right: 8, top: 4, left: 3, bottom: 4,
    },
  },
  maintainAspectRatio: false,
  scales: {
    yAxes: [{ ticks: { beginAtZero: true, display: false }, gridLines: { color: 'rgba(0,0,0,0.03)', drawBorder: false } }],
    xAxes: [{ type: 'time', gridLines: { color: 'rgba(0,0,0,0)', display: true }, display: false }],
  },
  legend: {
    display: false,
  },
  tooltips: {
    displayColors: false,
    callbacks: {
      title() { },
      label(tooltipItem, data) {
        const { label } = tooltipItem;
        const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
        return `${label}: ${value} clicks`;
      },
    },
  },
};

const AdminLink = ({
  user, link, deleteUrl, copyLink, chartData: getChartData, disableReferrals = false,
}) => {
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [referralInfo, setReferralInfo] = useState({ referrals: [], totals: {} });
  const [referralsLoading, setReferralsLoading] = useState(false);
  const linkId = link?._id;
  const orgId = user?.organisation?._id;
  const chartData = useMemo(() => getChartData(link), [link, getChartData]);
  const theme = useTheme();
  const qrRef = useRef(null);

  const qrCode = useMemo(() => new QRCodeStyling({
    width: 200,
    height: 200,
    type: 'svg',
    data: `${domain}/${link.path}`,
    image: AppIcon,
    dotsOptions: {
      color: '#14435B',
    },
    backgroundOptions: {
      color: '#ffffff',
    },
    imageOptions: {
      margin: 4,
    },
  }), [link]);

  const getReferrals = useCallback(() => {
    setReferralsLoading(true);
    api.organisations.getShortenedUrlReferrals(orgId, linkId, (data) => {
      setReferralInfo(data);
      setReferralsLoading(false);
    }, () => {
      setReferralsLoading(false);
      toast.error('Failed to get referrals');
    });
  }, [linkId, orgId]);

  useEffect(() => {
    if (referralModalOpen) {
      getReferrals();
    }
  }, [referralModalOpen, getReferrals]);

  useEffect(() => {
    if (qrCodeModalOpen) {
      qrCode.append(qrRef.current);
    }
  }, [qrCodeModalOpen, qrCode]);

  const downloadQrCode = useCallback(() => {
    qrCode.download({ name: `simpylydo-${link.path}`, extension: 'svg' });
  }, [qrCode, link]);

  const percentageReferrals = Math.round((referralInfo.referrals.length / referralInfo.totalClicks) * 100);
  return (
    <>
      <Modal
        style={{ marginTop: 50 }}
        open={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
      >
        <Modal.Header>
          Link analysis
        </Modal.Header>
        <Modal.Content>
          <Grid stackable>
            <Grid.Column width={6}>
              <p>{util.appName()} attempts to record user interaction with short links for the purposes of assessing the performance of campaigns.</p>
              <h4>Engagements</h4>
              <p>Here we use the term <em>engagements</em> to refer to people who continue to login or register after following a short link that points to the {util.appName()} platform.</p>
              <Divider hidden />
              <Message>Please note that SSO logins include registrations made via single sign-on too.</Message>
            </Grid.Column>
            <Grid.Column width={10}>
              {referralsLoading ? <h4>Loading...</h4>
                : <>
                  <h3>Engagement summary</h3>
                  <Statistic.Group size='small'>
                    <Statistic>
                      <Statistic.Value>{Object.values(referralInfo.totals).reduce((a, b) => a + b, 0)}</Statistic.Value>
                      <Statistic.Label>Engagements</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{Number.isNaN(percentageReferrals) ? 0 : percentageReferrals}%</Statistic.Value>
                      <Statistic.Label>Engagement rate</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{referralInfo.averageReferralTime?.slice(0, 7)}</Statistic.Value>
                      <Statistic.Label>Time from click<br />(secs)</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>

                  <Divider section />
                  <h3>Resumed session engagements</h3>
                  <p>When already-logged-in users resume a session after clicking the link.</p>
                  <Statistic.Group size='small'>
                    <Statistic>
                      <Statistic.Value>{referralInfo?.totals?.resumedSession || 0}</Statistic.Value>
                      <Statistic.Label>Resumed sessions</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>

                  <Divider section />
                  <h3>Login engagements</h3>
                  <p>When a user logs-in after clicking the link.</p>
                  <Statistic.Group size='small'>
                    <Statistic>
                      <Statistic.Value>{referralInfo?.totals?.loginToken || 0}</Statistic.Value>
                      <Statistic.Label>SSO logins</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{referralInfo?.totals?.login || 0}</Statistic.Value>
                      <Statistic.Label>Standard logins</Statistic.Label>
                    </Statistic>
                    <Statistic>
                      <Statistic.Value>{referralInfo?.totals?.otpLogin || 0}</Statistic.Value>
                      <Statistic.Label>2FA logins</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>

                  <Divider section />
                  <h3>Registration engagements</h3>
                  <p>When a new user registers after clicking the link.</p>
                  <Statistic.Group size='small'>
                    <Statistic>
                      <Statistic.Value>{referralInfo?.totals?.register || 0}</Statistic.Value>
                      <Statistic.Label>Registrations</Statistic.Label>
                    </Statistic>
                  </Statistic.Group>
                </>
              }
            </Grid.Column>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button icon='close' content='Close' onClick={() => setReferralModalOpen(false)} />
        </Modal.Actions>
      </Modal>
      <Modal
        style={{ marginTop: 50 }}
        open={qrCodeModalOpen}
        onClose={() => setQrCodeModalOpen(false)}
      >
        <Modal.Header>
          <h2>Link QR code</h2>
        </Modal.Header>
        <Modal.Content>
          <p>Download this QR code and share it with users to take them directly to the link.  Scanning the QR code using a camera will take you directly to the link.</p>
          <div style={{ width: '70%', margin: '10px auto', textAlign: 'center' }} ref={qrRef} />
        </Modal.Content>
        <Modal.Actions>
          <Button icon='close' content='Cancel' onClick={() => setQrCodeModalOpen(false)} />
          <ThemedButton icon='download' content='Download QR code' onClick={() => downloadQrCode()} />
        </Modal.Actions>
      </Modal>
      <Table.Row key={link._id}>
        <Table.Cell>
          <a href={`${domain}/${link.path}`} target='_blank' rel='noopener noreferrer'>{`${domain}/${link.path}`}</a>
          <span style={{ marginLeft: 5, cursor: 'pointer' }} data-tooltip='Copy to clipboard' onClick={() => copyLink(link.path)}><Icon name='copy outline' /></span>
          <br /><small style={{ overflowWrap: 'anywhere' }}>Resolves to: <a href={link.url} target='_blank' rel='noopener noreferrer'>{link.url}</a></small>
          <br /> <small style={{ overflowWrap: 'anywhere' }}>{link?.description}</small>
        </Table.Cell>
        {theme.sizes.isMobile
          ? (
            <>
              <Table.Cell>
                Created {moment(link.createdAt).fromNow()}, {link.clicks || 0} clicks
              </Table.Cell>
              <Table.Cell />
              <Table.Cell />
            </>
          ) : (
            <>
              <Table.Cell>{moment(link.createdAt).fromNow()}</Table.Cell>
              <Table.Cell>{link.clicks || 0}</Table.Cell>
              {chartData
                ? (
                  <Table.Cell>
                    <div style={{ width: 400, height: 75 }}>
                      <Line height={75} width={400} options={chartOptions} data={chartData} />
                    </div>
                  </Table.Cell>
                ) : null
              }
            </>
          )
        }
        <Table.Cell collapsing textAlign='right'>
          {!disableReferrals
            ? (
              <Popup
                on='hover'
                content='Show user capture information from this link'
                trigger={(
                  <Button
                    size='tiny'
                    icon='info'
                    onClick={() => setReferralModalOpen(true)}
                  />
                )}
              />
            ) : null
          }

          <Popup
            on='hover'
            content='Download a QR code to share this link with users. '
            trigger={(
              <Button
                size='tiny'
                icon='qrcode'
                onClick={() => setQrCodeModalOpen(true)}
              />
            )}
          />
          <Button size='tiny' basic icon='trash' onClick={() => deleteUrl(link._id)} />
        </Table.Cell>
      </Table.Row>
    </>
  );
};

export default connect((state) => ({ user: state.user }))(AdminLink);
